import React, { useState, useEffect, useRef } from 'react';
import { Power, PowerOff, Trash2, RefreshCw, Server, Activity, DollarSign, Clock, Settings, Globe, Users, Zap, Info, Terminal, AlertTriangle } from 'lucide-react';
import * as clusterService from '../services/clusterService';
import * as imageService from '../services/imageService';
import { useAuth } from '../hooks/useAuth';
import LaunchClusterModal from './LaunchClusterModal';
import ClusterSharingModal from './ClusterSharingModal';
import ClusterDetailModal from './ClusterDetailModal';
import ClusterLogsModal from './ClusterLogsModal';

const InferencePage = () => {
  const { user, isAdmin, isInferenceOnly } = useAuth();
  const [clusters, setClusters] = useState([]);
  // Clusters the orchestrator still holds that TCF has no row for. Admin-only,
  // and empty for everyone else.
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [sharingCluster, setSharingCluster] = useState(null);
  const [detailCluster, setDetailCluster] = useState(null);
  const [logsCluster, setLogsCluster] = useState(null);
  const [curatedImages, setCuratedImages] = useState([]);
  const [curatedDockerImages, setCuratedDockerImages] = useState(new Set());

  const autoRefreshRef = useRef(null);

  useEffect(() => {
    fetchClusters();
    fetchCuratedImages();
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, []);

  // Auto-refresh while there are INIT clusters
  useEffect(() => {
    const hasInit = clusters.some(c => c.status === 'INIT');
    if (hasInit && !autoRefreshRef.current) {
      autoRefreshRef.current = setInterval(async () => {
        try {
          const response = await clusterService.getClusterStatus();
          if (response.success) {
            setClusters(response.clusters || []);
            setOrphans(response.orphans || []);
          }
        } catch { /* ignore */ }
      }, 10000);
    } else if (!hasInit && autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current);
      autoRefreshRef.current = null;
    }
  }, [clusters]);

  const fetchClusters = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use getClusterStatus (polls orchestrator) so region/cloud get resolved from auto
      const response = await clusterService.getClusterStatus();
      if (response.success) {
        setClusters(response.clusters || []);
        setOrphans(response.orphans || []);
      }
    } catch (err) {
      console.error('Failed to fetch clusters:', err);
      setError(err.message || 'Failed to load endpoints');
    } finally {
      setLoading(false);
    }
  };

  const fetchCuratedImages = async () => {
    try {
      const data = await imageService.listCuratedImages();
      if (data.success && data.images) {
        setCuratedImages(data.images);
        const dockerSet = new Set();
        data.images.forEach(img => {
          if (img.docker_image) dockerSet.add(img.docker_image);
          if (img.tcf_image) dockerSet.add(img.tcf_image);
        });
        setCuratedDockerImages(dockerSet);
      }
    } catch { /* ignore */ }
  };

  const findCuratedImage = (dockerImageUrl) => {
    return curatedImages.find(img => img.docker_image === dockerImageUrl || img.tcf_image === dockerImageUrl);
  };

  const refreshClusterStatus = async () => {
    try {
      setRefreshing(true);
      const response = await clusterService.getClusterStatus();
      if (response.success) {
        setClusters(response.clusters || []);
      }
    } catch (err) {
      console.error('Failed to refresh status:', err);
      setError(err.message || 'Failed to refresh status');
    } finally {
      setRefreshing(false);
    }
  };

  const handleLaunchCluster = async (clusterConfig) => {
    try {
      const response = await clusterService.launchCluster(clusterConfig);
      if (response.success) {
        setShowLaunchModal(false);
        await fetchClusters();
        return { success: true };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleStopCluster = async (clusterId, clusterName) => {
    if (!window.confirm(`Are you sure you want to stop "${clusterName}"? This will preserve data but stop compute resources.`)) return;
    try {
      const response = await clusterService.stopCluster(clusterId);
      if (response.success) await fetchClusters();
    } catch (err) {
      alert(`Failed to stop: ${err.message}`);
    }
  };

  const handleStartCluster = async (clusterId) => {
    try {
      const response = await clusterService.startCluster(clusterId);
      if (response.success) await fetchClusters();
    } catch (err) {
      alert(`Failed to start: ${err.message}`);
    }
  };

  const handleDownCluster = async (clusterId, clusterName) => {
    if (!window.confirm(`Are you sure you want to TEAR DOWN "${clusterName}"? This will destroy ALL resources and data. This action CANNOT be undone!`)) return;
    const confirmText = window.prompt(`Type "${clusterName}" to confirm teardown:`);
    if (confirmText !== clusterName) {
      alert('Teardown cancelled: name did not match');
      return;
    }
    try {
      const response = await clusterService.downCluster(clusterId);
      if (response.success) await fetchClusters();
    } catch (err) {
      alert(`Failed to tear down: ${err.message}`);
    }
  };

  const handleGracefulDown = async (clusterId, clusterName) => {
    if (!window.confirm(
      `Gracefully tear down "${clusterName}"?\n\n` +
      `New requests will stop being routed to it immediately, and it will be ` +
      `destroyed automatically once the current queue is empty. This cannot be undone.`
    )) return;
    try {
      const response = await clusterService.gracefulDownCluster(clusterId);
      if (response.success) {
        await fetchClusters();
        alert(response.message || 'Draining started; the cluster will be torn down once idle.');
      }
    } catch (err) {
      alert(`Failed to start graceful teardown: ${err.message}`);
    }
  };

  const hasPermission = (cluster, perm) => (cluster.permissions || []).includes(perm);
  const isOwner = (cluster) => cluster.user_id === user?.id;
  /**
   * Billed rate for the whole cluster. total_hourly_rate_cents comes from the
   * same markup chain the billing cron uses; the older fields are a fallback,
   * and estimated_cost_per_hour is raw cloud cost (no markup) so it is last.
   */
  const formatCost = (cluster) => {
    const nodes = Math.max(1, cluster.num_nodes || 1);
    let cents = cluster.total_hourly_rate_cents;
    if (cents == null && cluster.pricing_rates?.per_server_hour != null) {
      cents = Math.round(parseFloat(cluster.pricing_rates.per_server_hour) * 100) * nodes;
    }
    if (cents == null && cluster.estimated_cost_per_hour) {
      cents = Math.round(parseFloat(cluster.estimated_cost_per_hour) * 100) * nodes;
    }
    return cents ? `$${(cents / 100).toFixed(2)}/hr` : 'N/A';
  };
  const calculateUptime = (launchedAt) => {
    if (!launchedAt) return 'N/A';
    const diffMs = Date.now() - new Date(launchedAt).getTime();
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const statusConfig = {
    UP: { color: 'bg-green-500', ring: 'ring-green-200', text: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', label: 'Running' },
    INIT: { color: 'bg-yellow-500', ring: 'ring-yellow-200', text: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'Initializing' },
    STOPPED: { color: 'bg-gray-400', ring: 'ring-gray-200', text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Stopped' },
    DOWN: { color: 'bg-red-500', ring: 'ring-red-200', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'Down' },
    FAILED: { color: 'bg-red-500', ring: 'ring-red-200', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'Failed' },
    // The row lives on after teardown is accepted, until the cron confirms the
    // cluster is actually gone. Deleting it on acceptance alone is what used to
    // hide clusters that were still running.
    TERMINATING: { color: 'bg-orange-500', ring: 'ring-orange-200', text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Terminating' },
  };

  const upCount = clusters.filter(c => c.status === 'UP').length;
  const initCount = clusters.filter(c => c.status === 'INIT').length;
  const stoppedCount = clusters.filter(c => c.status === 'STOPPED').length;

  return (
    <div className="space-y-6">
      {/* Clusters the orchestrator holds that TCF has no row for.
          They bill, and they own a name the next launch will collide with —
          and they appear nowhere else in the UI, which is exactly how one of
          them sat unnoticed for days and deadlocked a later launch. */}
      {orphans.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900">
                {orphans.length} cluster{orphans.length > 1 ? 's' : ''} running on the
                orchestrator with no record here
              </div>
              <div className="text-amber-800 mt-1">
                These are still billing and their names are taken — launching with the
                same name will collide. Tear them down from the orchestrator.
              </div>
              <ul className="mt-2 space-y-0.5 font-mono text-xs text-amber-900">
                {orphans.map((o) => (
                  <li key={o.cluster_name}>
                    {o.cluster_name} — {o.status}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{isInferenceOnly ? 'Endpoints' : 'Inference'}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {isInferenceOnly
              ? 'Endpoints available to your account. Call them from the Playground or the API.'
              : 'Manage inference endpoints and test models'}
          </p>
        </div>
      </div>

      {/* Endpoints */}
          {/* Status summary + actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {upCount > 0 && (
                <div className="flex items-center space-x-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500 ring-2 ring-green-200" />
                  <span className="text-sm text-gray-600">{upCount} running</span>
                </div>
              )}
              {initCount > 0 && (
                <div className="flex items-center space-x-1.5">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 ring-2 ring-yellow-200 animate-pulse" />
                  <span className="text-sm text-gray-600">{initCount} initializing</span>
                </div>
              )}
              {stoppedCount > 0 && (
                <div className="flex items-center space-x-1.5">
                  <div className="w-2 h-2 rounded-full bg-gray-400 ring-2 ring-gray-200" />
                  <span className="text-sm text-gray-600">{stoppedCount} stopped</span>
                </div>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={refreshClusterStatus}
                disabled={refreshing}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2 disabled:opacity-50 text-sm"
              >
                <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>
              {!isInferenceOnly && (
                <button
                  onClick={() => setShowLaunchModal(true)}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm"
                >
                  <Zap size={15} />
                  <span>Deploy Endpoint</span>
                </button>
              )}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="animate-spin text-purple-600" size={32} />
              <span className="ml-3 text-gray-600">Loading endpoints...</span>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Error: {error}</p>
              <button onClick={fetchClusters} className="mt-2 text-sm text-red-600 underline">Try again</button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && clusters.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Server size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No inference endpoints</h3>
              {isInferenceOnly ? (
                <p className="text-gray-600">
                  No endpoints have been shared with your account yet. Contact your administrator to get access.
                </p>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">Deploy a model from the Image catalog to create an endpoint</p>
                  <button
                    onClick={() => setShowLaunchModal(true)}
                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center space-x-2"
                  >
                    <Zap size={16} />
                    <span>Deploy Your First Endpoint</span>
                  </button>
                </>
              )}
            </div>
          )}

          {/* Endpoint Cards */}
          {!loading && clusters.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {clusters.map(cluster => {
                const sc = statusConfig[cluster.status] || statusConfig.DOWN;
                return (
                  <div key={cluster.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-bold text-lg">{cluster.cluster_name}</h3>
                            {isAdmin && (
                              <button onClick={() => setSharingCluster(cluster)} className="text-purple-200 hover:text-white transition-colors" title="Sharing settings">
                                <Settings size={14} />
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-purple-100 mt-1">
                            {cluster.cloud_provider?.toUpperCase() || 'Cloud'} {cluster.region && `• ${cluster.region}`}
                          </p>
                          {cluster.owner_email && !isOwner(cluster) && (
                            <p className="text-xs text-purple-200 mt-1">Owner: {cluster.owner_email}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          {/* Status flag */}
                          <div className={`flex items-center space-x-1.5 px-3 py-1 rounded-full ${sc.bg} ${sc.border} border`}>
                            <div className={`w-2 h-2 rounded-full ${sc.color} ${cluster.status === 'INIT' ? 'animate-pulse' : ''}`} />
                            <span className={`text-xs font-semibold ${sc.text}`}>{sc.label}</span>
                          </div>
                          {cluster.visibility && cluster.visibility !== 'private' && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center space-x-1 ${
                              cluster.visibility === 'public'
                                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                                : 'bg-orange-100 text-orange-800 border border-orange-300'
                            }`}>
                              {cluster.visibility === 'public' ? <Globe size={10} /> : <Users size={10} />}
                              <span>{cluster.visibility === 'public' ? 'Public' : 'Shared'}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="px-6 py-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center space-x-2">
                          <Server size={14} className="text-gray-500" />
                          <span className="text-gray-600">Instance:</span>
                          <span className="font-medium">{cluster.instance_type || 'Auto'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Activity size={14} className="text-gray-500" />
                          <span className="text-gray-600">GPUs:</span>
                          <span className="font-medium">
                            {cluster.accelerator_count > 0 ? `${cluster.accelerator_count}x ${cluster.accelerator_type}` : 'None'}
                          </span>
                        </div>
                      </div>

                      {hasPermission(cluster, 'cost') && (
                        <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-gray-100">
                          <div className="flex items-center space-x-2">
                            <DollarSign size={14} className="text-green-500" />
                            <span className="text-gray-600">Cost:</span>
                            <span className="font-medium text-green-600">{formatCost(cluster)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock size={14} className="text-blue-500" />
                            <span className="text-gray-600">Uptime:</span>
                            <span className="font-medium">{cluster.status === 'UP' ? calculateUptime(cluster.launched_at) : 'N/A'}</span>
                          </div>
                        </div>
                      )}

                      {cluster.pricing_rates && typeof cluster.pricing_rates === 'object' && Object.keys(cluster.pricing_rates).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
                          <span className="text-[10px] text-gray-500 mr-1">TCF Rates:</span>
                          {cluster.pricing_rates.per_server_hour != null && (
                            <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200">${cluster.pricing_rates.per_server_hour}/hr</span>
                          )}
                          {cluster.pricing_rates.per_inference_run != null && (
                            <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">${cluster.pricing_rates.per_inference_run}/run</span>
                          )}
                          {cluster.pricing_rates.per_inference_minute != null && (
                            <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">${cluster.pricing_rates.per_inference_minute}/min</span>
                          )}
                          {cluster.pricing_rates.per_input_mb != null && (
                            <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded border border-yellow-200">${cluster.pricing_rates.per_input_mb}/MB in</span>
                          )}
                          {cluster.pricing_rates.per_output_mb != null && (
                            <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded border border-yellow-200">${cluster.pricing_rates.per_output_mb}/MB out</span>
                          )}
                        </div>
                      )}

                      {cluster.num_nodes > 1 && (
                        <div className="text-xs text-gray-600 bg-blue-50 px-3 py-2 rounded">
                          Multi-node: {cluster.num_nodes} nodes
                        </div>
                      )}

                      {cluster.autoscaling_config && (
                        <div className="text-xs text-blue-700 bg-blue-50 px-3 py-2 rounded flex items-center space-x-1.5">
                          <span>Autoscaling: {cluster.autoscaling_config.min_nodes}–{cluster.autoscaling_config.max_nodes} nodes</span>
                          <span className="text-blue-400">·</span>
                          <span className="capitalize">{(cluster.autoscaling_config.strategy || '').replace('_', ' ')}</span>
                          <span className="text-[10px] px-1 py-0.5 rounded bg-blue-100 text-blue-600 font-medium ml-1">Preview</span>
                        </div>
                      )}

                      {cluster.autostop > 0 && (
                        <div className="text-xs text-gray-600 bg-yellow-50 px-3 py-2 rounded">
                          Auto-stop: {cluster.autostop} min idle
                        </div>
                      )}
                    </div>

                    {/* Failed Launch Error */}
                    {cluster.status === 'FAILED' && (
                      <div className="mx-6 mb-2 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                        <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-800">Launch failed</p>
                          {cluster.error && (
                            <p className="text-xs text-red-600 mt-0.5 break-words">{cluster.error}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-wrap gap-2">
                      {/* Inference-only accounts get no lifecycle controls, and no
                          Details/Logs either: both read provisioning logs, which
                          the role's backend allowlist does not include. */}
                      {isInferenceOnly && (
                        <div className="flex-1 text-center text-sm text-gray-500 py-2">
                          View only — call this endpoint from the Playground
                        </div>
                      )}
                      {!isInferenceOnly && (
                        <button
                          onClick={() => setDetailCluster(cluster)}
                          className="flex-1 min-w-[7rem] whitespace-nowrap bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm font-medium hover:bg-gray-300 transition-colors flex items-center justify-center space-x-1"
                          title="View details"
                        >
                          <Info size={14} />
                          <span>Details</span>
                        </button>
                      )}
                      {!isInferenceOnly && cluster.status === 'UP' && (
                        <button
                          onClick={() => setLogsCluster(cluster)}
                          className="flex-1 min-w-[7rem] whitespace-nowrap bg-gray-800 text-green-400 px-3 py-2 rounded text-sm font-medium hover:bg-gray-700 transition-colors flex items-center justify-center space-x-1"
                          title="View runtime logs"
                        >
                          <Terminal size={14} />
                          <span>Logs</span>
                        </button>
                      )}
                      {!isInferenceOnly && cluster.status === 'UP' && hasPermission(cluster, 'stop') && (
                        <button
                          onClick={() => handleStopCluster(cluster.id, cluster.cluster_name)}
                          className="flex-1 min-w-[7rem] whitespace-nowrap bg-yellow-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-yellow-600 transition-colors flex items-center justify-center space-x-1"
                        >
                          <PowerOff size={14} />
                          <span>Stop</span>
                        </button>
                      )}
                      {!isInferenceOnly && cluster.status === 'STOPPED' && hasPermission(cluster, 'start') && (
                        <button
                          onClick={() => handleStartCluster(cluster.id)}
                          className="flex-1 min-w-[7rem] whitespace-nowrap bg-green-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-600 transition-colors flex items-center justify-center space-x-1"
                        >
                          <Power size={14} />
                          <span>Start</span>
                        </button>
                      )}
                      {!isInferenceOnly && hasPermission(cluster, 'teardown') && cluster.status === 'UP' && (
                        <button
                          onClick={() => handleGracefulDown(cluster.id, cluster.cluster_name)}
                          title="Stop routing new requests, then tear down once the queue is empty"
                          className="flex-1 min-w-[7rem] whitespace-nowrap bg-amber-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-amber-600 transition-colors flex items-center justify-center space-x-1"
                        >
                          <Clock size={14} />
                          <span>Drain &amp; stop</span>
                        </button>
                      )}
                      {!isInferenceOnly && hasPermission(cluster, 'teardown') && (
                        <button
                          onClick={() => handleDownCluster(cluster.id, cluster.cluster_name)}
                          className="flex-1 min-w-[7rem] whitespace-nowrap bg-red-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-red-600 transition-colors flex items-center justify-center space-x-1"
                        >
                          <Trash2 size={14} />
                          <span>Teardown</span>
                        </button>
                      )}
                      {!isInferenceOnly && !hasPermission(cluster, 'stop') && !hasPermission(cluster, 'start') && !hasPermission(cluster, 'teardown') && (
                        <div className="flex-1 text-center text-sm text-gray-500 py-2">View only</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

      {/* Modals */}
      {showLaunchModal && (
        <LaunchClusterModal
          onClose={() => setShowLaunchModal(false)}
          onLaunch={handleLaunchCluster}
          isAdmin={isAdmin}
        />
      )}
      {sharingCluster && (
        <ClusterSharingModal
          cluster={sharingCluster}
          curatedImage={findCuratedImage(sharingCluster.image_id)}
          onClose={() => setSharingCluster(null)}
          onUpdated={() => { setSharingCluster(null); fetchClusters(); }}
          onPlaygroundSaved={fetchCuratedImages}
        />
      )}
      {detailCluster && (
        <ClusterDetailModal
          cluster={detailCluster}
          onClose={() => { setDetailCluster(null); fetchClusters(); }}
          onTeardown={(id, name) => { setDetailCluster(null); handleDownCluster(id, name); }}
        />
      )}
      {logsCluster && (
        <ClusterLogsModal
          cluster={logsCluster}
          onClose={() => setLogsCluster(null)}
        />
      )}
    </div>
  );
};

export default InferencePage;
